import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	BankProductFormInput,
	BankProductFormOutput,
} from "web_myplann/Portfolio/Forms/BankProductForm/bankProductFormSchema";
import {
	CONTRACT_REQUIRED_MINIMUM_FIELDS,
	getContractRequiredMinimumMessage,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import { useProducerProductOptions } from "web_myplann/Portfolio/Helpers/useProducerProductOptions";
import { useProductCategoryOptions } from "web_myplann/Portfolio/Helpers/useProductCategoryOptions";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { FlatRowFormCombobox } from "web_myplann/Shared/Components/FlatRow/FlatRowFormCombobox";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type BankProductFormFieldsProps = {
	control: Control<BankProductFormInput, unknown, BankProductFormOutput>;
};

export const BankProductFormFields = ({
	control,
}: BankProductFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const categoryOptions = useProductCategoryOptions(
		ClientPortfolioDomainEnum.BankingProducts,
	);

	const { producerOptions, productOptions, isLoadingProducts } =
		useProducerProductOptions(
			control,
			ClientPortfolioDomainEnum.BankingProducts,
		);

	return (
		<>
			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddBankProduct.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Portfolio.AddBankProduct.Fields.name)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="contractNumber"
					label={t(Resources.Portfolio.AddBankProduct.Fields.contractNumber)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormCombobox
					control={control}
					name="producer"
					label={t(Resources.Portfolio.AddBankProduct.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProducer,
					)}
					options={producerOptions}
				/>
				<FlatRowFormCombobox
					control={control}
					name="product"
					label={t(Resources.Portfolio.AddBankProduct.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProduct,
					)}
					options={productOptions}
					isLoading={isLoadingProducts}
				/>
				<FlatRowFormSelect
					control={control}
					name="productCategoryId"
					label={t(Resources.Portfolio.AddBankProduct.Fields.productCategory)}
					options={categoryOptions}
					isLast
				/>
			</Stack>
			<RequiredMinimumMessage
				control={control}
				fields={CONTRACT_REQUIRED_MINIMUM_FIELDS}
				message={getContractRequiredMinimumMessage(
					t,
					Resources.Portfolio.AddBankProduct.Fields,
				)}
			/>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddBankProduct.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name="signedAt"
					label={t(Resources.Portfolio.AddBankProduct.Fields.signedAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="startAt"
					label={t(Resources.Portfolio.AddBankProduct.Fields.startAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="endAt"
					label={t(Resources.Portfolio.AddBankProduct.Fields.endAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>
		</>
	);
};
